import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, FileText, BarChart3, Clock, Receipt, Send, CheckCircle, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const PERIODS = [{ value: 'all', label: 'All Time' }, { value: 'year', label: 'This Year' }, { value: 'quarter', label: 'This Quarter' }, { value: 'month', label: 'This Month' }];

export default function FinancialDashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('year');
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [gstReport, setGstReport] = useState(null);
  const [gstDateFrom, setGstDateFrom] = useState(() => { const n = new Date(); return n.getMonth() >= 3 ? `${n.getFullYear()}-04-01` : `${n.getFullYear()-1}-04-01`; });
  const [gstDateTo, setGstDateTo] = useState(() => { const n = new Date(); return n.getMonth() >= 3 ? `${n.getFullYear()+1}-03-31` : `${n.getFullYear()}-03-31`; });
  const [gstPeriod, setGstPeriod] = useState('');
  const [gstSettingsLoaded, setGstSettingsLoaded] = useState(false);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Load GST settings on mount
  useEffect(() => {
    api.get('/report-config').then(({ data }) => {
      if (data?.gst_filing_frequency && !gstSettingsLoaded) {
        setGstPeriod(String(data.gst_filing_frequency));
        setGstSettingsLoaded(true);
      }
    }).catch(() => {});
  }, [api, gstSettingsLoaded]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, tr, od] = await Promise.all([
        api.get(`/financial/overview?period=${period}`),
        api.get('/financial/monthly-trend'),
        api.get('/financial/overdue'),
      ]);
      setOverview(ov.data);
      setTrend((tr.data || []).reverse());
      setOverdue(od.data || []);
      setOverdue(od.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, period]);

  const loadGst = useCallback(async () => {
    try {
      const params = new URLSearchParams({ date_from: gstDateFrom, date_to: gstDateTo });
      if (gstPeriod) params.set('gst_period', gstPeriod);
      const { data } = await api.get(`/financial/gst-report?${params}`);
      setGstReport(data);
    } catch (e) { console.error(e); }
  }, [api, gstDateFrom, gstDateTo, gstPeriod]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'gst') loadGst(); }, [tab, loadGst]);

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtK = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const fmtMonth = (m) => {
    if (!m) return '';
    try { const [y, mo] = m.split('-'); return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(mo)-1]} ${y}`; }
    catch { return m; }
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    try { const [y,m,dd] = d.slice(0,10).split('-'); return `${dd}/${m}/${y}`; } catch { return d; }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading financial data...</div>;

  return (
    <div data-testid="financial-dashboard-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Financials</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Invoice revenue, GST reporting & pipeline</p>
        </div>
        <div className="flex gap-1 bg-white border border-[#E2E2DF] rounded-md p-0.5 shadow-sm">
          <button onClick={() => setTab('overview')} className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${tab === 'overview' ? 'bg-[#D35400] text-white' : 'text-slate-500 hover:text-slate-700'}`}>Overview</button>
          <button onClick={() => setTab('gst')} className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${tab === 'gst' ? 'bg-[#D35400] text-white' : 'text-slate-500 hover:text-slate-700'}`}>GST Report</button>
        </div>
      </div>

      {tab === 'overview' && overview && (
        <>
          {/* Period selector */}
          <div className="flex gap-1 bg-slate-50 rounded-md p-0.5 w-fit">
            {PERIODS.map(p => (
              <button key={p.value} onClick={() => setPeriod(p.value)} className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${period === p.value ? 'bg-white text-[#D35400] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{p.label}</button>
            ))}
          </div>

          {/* Invoice Status Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Created */}
            <div className="bg-white border border-blue-100 rounded-md overflow-hidden relative card-lift">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-blue-600" />
              <div className="flex items-center justify-between px-4 py-3 bg-blue-50/60 border-b border-blue-100">
                <div className="flex items-center gap-2">
                  <FileText size={15} className="text-blue-600" />
                  <span className="text-sm font-bold text-blue-800 tracking-tight">Created</span>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white text-blue-700 ring-1 ring-blue-100 font-mono-num">{overview.created_count}</span>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex justify-between text-xs"><span className="text-slate-500">Excl. GST</span><span className="font-semibold text-slate-800 font-mono-num">{fmtK(overview.created_excl_gst)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">GST</span><span className="font-semibold text-slate-600 font-mono-num">{fmtK(overview.created_gst)}</span></div>
                <div className="flex justify-between text-xs border-t border-blue-100 pt-2"><span className="text-slate-700 font-semibold">Total (incl GST)</span><span className="font-bold text-blue-700 font-mono-num">{fmtK(overview.created_total)}</span></div>
              </div>
            </div>

            {/* Sent */}
            <div className="bg-white border border-amber-100 rounded-md overflow-hidden relative card-lift">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-amber-500" />
              <div className="flex items-center justify-between px-4 py-3 bg-amber-50/60 border-b border-amber-100">
                <div className="flex items-center gap-2">
                  <Send size={15} className="text-amber-600" />
                  <span className="text-sm font-bold text-amber-800 tracking-tight">Sent</span>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white text-amber-700 ring-1 ring-amber-100 font-mono-num">{overview.sent_count}</span>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex justify-between text-xs"><span className="text-slate-500">Excl. GST</span><span className="font-semibold text-slate-800 font-mono-num">{fmtK(overview.sent_excl_gst)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">GST</span><span className="font-semibold text-slate-600 font-mono-num">{fmtK(overview.sent_gst)}</span></div>
                <div className="flex justify-between text-xs border-t border-amber-100 pt-2"><span className="text-slate-700 font-semibold">Total (incl GST)</span><span className="font-bold text-amber-700 font-mono-num">{fmtK(overview.sent_total)}</span></div>
              </div>
            </div>

            {/* Paid */}
            <div className="bg-white border border-emerald-100 rounded-md overflow-hidden relative card-lift">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-emerald-600" />
              <div className="flex items-center justify-between px-4 py-3 bg-emerald-50/60 border-b border-emerald-100">
                <div className="flex items-center gap-2">
                  <CheckCircle size={15} className="text-emerald-600" />
                  <span className="text-sm font-bold text-emerald-800 tracking-tight">Paid</span>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white text-emerald-700 ring-1 ring-emerald-100 font-mono-num">{overview.paid_count}</span>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex justify-between text-xs"><span className="text-slate-500">Excl. GST</span><span className="font-semibold text-slate-800 font-mono-num">{fmtK(overview.paid_excl_gst)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">GST</span><span className="font-semibold text-slate-600 font-mono-num">{fmtK(overview.paid_gst)}</span></div>
                <div className="flex justify-between text-xs border-t border-emerald-100 pt-2"><span className="text-slate-700 font-semibold">Total (incl GST)</span><span className="font-bold text-emerald-700 font-mono-num">{fmtK(overview.total_paid)}</span></div>
              </div>
            </div>
          </div>

          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
              <div className="flex items-center justify-between mb-2"><span className="eyebrow">Total Invoiced</span><Receipt size={16} className="text-[#D35400]" /></div>
              <p className="text-2xl font-extrabold text-slate-900 font-[Manrope] font-mono-num tracking-tight">{fmtK(overview.total_invoiced)}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono-num">{overview.invoice_count} invoices</p>
            </div>
            <div className="bg-white border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-rose-500" />
              <div className="flex items-center justify-between mb-2"><span className="eyebrow text-rose-500">Expenses</span><Receipt size={16} className="text-rose-500" /></div>
              <p className="text-2xl font-extrabold text-rose-600 font-[Manrope] font-mono-num tracking-tight">{fmtK(overview.total_expenses)}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono-num">{overview.expense_count} expenses · GST {fmtK(overview.expenses_gst)}</p>
            </div>
            <div className="bg-white border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-emerald-600" />
              <div className="flex items-center justify-between mb-2"><span className="eyebrow">Net Profit</span><TrendingUp size={16} className="text-emerald-600" /></div>
              <p className={`text-2xl font-extrabold font-[Manrope] font-mono-num tracking-tight ${(overview.net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtK(overview.net_profit)}</p>
              <p className="text-xs text-slate-500 mt-1">Paid revenue − Expenses (excl GST)</p>
            </div>
            <div className="bg-white border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-blue-600" />
              <div className="flex items-center justify-between mb-2"><span className="eyebrow">Pipeline</span><BarChart3 size={16} className="text-blue-600" /></div>
              <p className="text-2xl font-extrabold text-blue-700 font-[Manrope] font-mono-num tracking-tight">{fmtK(overview.pipeline_value)}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono-num">{overview.pipeline_count} quoted/accepted</p>
            </div>
          </div>

          {/* GST Summary — based on PAID only */}
          <div className="bg-white border border-[#E2E2DF] rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-base font-semibold text-slate-800">GST Summary</h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">Based on Paid Invoices</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <span className="text-xs text-green-600 uppercase font-bold">Output GST (Paid)</span>
                <p className="text-lg font-bold text-green-700 mt-1">{fmt(overview.paid_gst)}</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <span className="text-xs text-red-600 uppercase font-bold">Input GST (Expenses)</span>
                <p className="text-lg font-bold text-red-700 mt-1">{fmt(overview.expenses_gst)}</p>
              </div>
              <div className="text-center p-3 bg-[#D35400]/5 rounded-lg border-2 border-[#D35400]/20">
                <span className="text-xs text-[#D35400] uppercase font-bold">Net GST to Pay</span>
                <p className="text-lg font-bold text-[#D35400] mt-1">{fmt((overview.paid_gst || 0) - (overview.expenses_gst || 0))}</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <span className="text-xs text-amber-600 uppercase font-bold">Outstanding GST</span>
                <p className="text-lg font-bold text-amber-700 mt-1">{fmt(overview.outstanding_gst)}</p>
                <p className="text-[10px] text-amber-500 mt-0.5">Created + Sent</p>
              </div>
            </div>
          </div>

          {/* Monthly Trend */}
          {trend.length > 0 && (
            <div className="bg-white border border-[#E2E2DF] rounded-lg p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Monthly Invoice Trend</h3>
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={trend}>
                    <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={v => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmt(v)} labelFormatter={fmtMonth} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="paid" name="Paid" stackId="stack" fill="#16a34a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="sent" name="Sent" stackId="stack" fill="#d97706" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="created" name="Created" stackId="stack" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Overdue Invoices */}
          <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-red-50 border-b border-red-200">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-red-600" />
                <span className="text-sm font-bold text-red-800">Overdue</span>
                <span className="text-[10px] text-red-500 font-medium">Sent &gt; 60 days, unpaid</span>
              </div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white text-red-700">{overdue.length}</span>
            </div>
            {overdue.length > 0 ? (
              <div className="overflow-auto" style={{ maxHeight: '300px' }}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-[#E2E2DF] sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-4 py-2 text-[10px] font-bold uppercase text-slate-400">Invoice</th>
                      <th className="text-left px-4 py-2 text-[10px] font-bold uppercase text-slate-400">Date Sent</th>
                      <th className="text-left px-4 py-2 text-[10px] font-bold uppercase text-slate-400">Client</th>
                      <th className="text-left px-4 py-2 text-[10px] font-bold uppercase text-slate-400">Property</th>
                      <th className="text-right px-4 py-2 text-[10px] font-bold uppercase text-slate-400">Amount</th>
                      <th className="text-right px-4 py-2 text-[10px] font-bold uppercase text-red-400">Days Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdue.map(inv => {
                      const days = Math.floor((Date.now() - new Date(inv.invoice_date).getTime()) / 86400000);
                      return (
                        <tr key={inv._id} onClick={() => navigate(`/invoices/${inv._id}`)} className="border-t border-[#E2E2DF] hover:bg-red-50/50 cursor-pointer transition-colors">
                          <td className="px-4 py-2.5 font-semibold text-[#D35400] text-xs">{inv.invoice_number}</td>
                          <td className="px-4 py-2.5 text-slate-600 text-xs">{fmtDate(inv.invoice_date)}</td>
                          <td className="px-4 py-2.5 text-slate-800 font-medium text-xs">{inv.client_name}</td>
                          <td className="px-4 py-2.5 text-slate-600 text-xs">{inv.property_address}</td>
                          <td className="px-4 py-2.5 text-right font-semibold font-mono-num text-xs">{fmt(inv.total_inc_gst)}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-red-600 text-xs">{days}d</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-6 text-center text-slate-300 text-xs">No overdue invoices</div>
            )}
          </div>
        </>
      )}

      {tab === 'gst' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E2E2DF] rounded-lg p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Date From</label>
                <input type="date" value={gstDateFrom} onChange={e => setGstDateFrom(e.target.value)} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Date To</label>
                <input type="date" value={gstDateTo} onChange={e => setGstDateTo(e.target.value)} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Filing Frequency</label>
                <select value={gstPeriod} onChange={e => setGstPeriod(e.target.value)} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white">
                  <option value="1">Monthly</option>
                  <option value="2">2-Monthly</option>
                  <option value="6">6-Monthly</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { const n = new Date(); const y = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear()-1; setGstDateFrom(`${y}-04-01`); setGstDateTo(`${y+1}-03-31`); }} className="text-xs px-3 py-2 border border-[#E2E2DF] rounded-lg text-slate-600 hover:bg-slate-50">Current Tax Year</button>
                <button onClick={() => { const n = new Date(); const y = n.getMonth() >= 3 ? n.getFullYear()-1 : n.getFullYear()-2; setGstDateFrom(`${y}-04-01`); setGstDateTo(`${y+1}-03-31`); }} className="text-xs px-3 py-2 border border-[#E2E2DF] rounded-lg text-slate-600 hover:bg-slate-50">Last Tax Year</button>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
            <p className="text-xs text-amber-700 font-medium">GST calculations are based on <strong>Paid invoices only</strong>. Unpaid invoices (Created/Sent) are excluded from Output GST and Net GST figures.</p>
          </div>

          {gstReport && (
            <>
              <div className="bg-white border border-[#E2E2DF] rounded-lg p-5">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <h3 className="text-base font-semibold text-slate-800">GST Summary — Inland Revenue</h3>
                    <p className="text-xs text-slate-400 mb-3">{gstReport.yearly.date_from ? (() => { try { const [y,m,d] = gstReport.yearly.date_from.split('-'); const [y2,m2,d2] = gstReport.yearly.date_to.split('-'); return `${d}/${m}/${y} to ${d2}/${m2}/${y2}`; } catch { return ''; } })() : ''} | {gstReport.yearly.gst_period === 1 ? 'Monthly' : gstReport.yearly.gst_period === 2 ? '2-Monthly' : '6-Monthly'} filing</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Current period GST101A */}
                    {(() => {
                      const now = new Date();
                      const currentPeriod = (gstReport.periods || []).find(p => p.start <= now.toISOString().slice(0,10) && p.end >= now.toISOString().slice(0,10));
                      if (currentPeriod) return (
                        <button
                          data-testid="generate-gst101a-current"
                          onClick={() => navigate(`/pdf-viewer/gst-form/${currentPeriod.start}_${currentPeriod.end}`)}
                          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-[#003366] text-white hover:bg-[#004488] transition-all font-semibold"
                        >
                          <FileText size={14} /> GST101A (Current Period)
                        </button>
                      );
                      return null;
                    })()}
                    <button
                      data-testid="generate-gst101a-yearly"
                      onClick={() => navigate(`/pdf-viewer/gst-form/${gstDateFrom}_${gstDateTo}`)}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-[#003366] text-[#003366] hover:bg-blue-50 transition-all font-semibold"
                    >
                      <FileText size={14} /> Full Year Summary
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg"><span className="text-[10px] text-blue-600 uppercase font-bold">Sales (excl GST)</span><p className="text-base font-bold text-blue-700 mt-1">{fmt(gstReport.yearly.total_subtotal)}</p></div>
                  <div className="text-center p-3 bg-green-50 rounded-lg"><span className="text-[10px] text-green-600 uppercase font-bold">Paid Sales</span><p className="text-base font-bold text-green-700 mt-1">{fmt(gstReport.yearly.total_paid_subtotal)}</p></div>
                  <div className="text-center p-3 bg-[#D35400]/5 rounded-lg"><span className="text-[10px] text-[#D35400] uppercase font-bold">Output GST (Paid)</span><p className="text-base font-bold text-[#D35400] mt-1">{fmt(gstReport.yearly.total_gst)}</p></div>
                  <div className="text-center p-3 bg-red-50 rounded-lg"><span className="text-[10px] text-red-600 uppercase font-bold">Expenses (excl GST)</span><p className="text-base font-bold text-red-700 mt-1">{fmt(gstReport.yearly.total_expense_excl_gst)}</p></div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg"><span className="text-[10px] text-purple-600 uppercase font-bold">Input GST</span><p className="text-base font-bold text-purple-700 mt-1">{fmt(gstReport.yearly.total_expense_gst)}</p></div>
                  <div className="text-center p-3 bg-green-50 rounded-lg border-2 border-green-200"><span className="text-[10px] text-green-600 uppercase font-bold">Net GST to Pay</span><p className="text-lg font-bold text-green-700 mt-1">{fmt(gstReport.yearly.total_net_gst)}</p></div>
                </div>
              </div>

              <div className="bg-white border border-[#E2E2DF] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-[#E2E2DF]">
                    <tr>
                      <th className="text-left px-3 py-2.5 text-xs font-bold uppercase text-slate-500">Period</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold uppercase text-slate-500">All Sales</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold uppercase text-green-700">Paid Sales</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold uppercase text-[#D35400]">Output GST</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold uppercase text-slate-500">Expenses</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold uppercase text-purple-600">Input GST</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold uppercase text-green-700 bg-green-50">Net GST</th>
                      <th className="text-center px-3 py-2.5 text-xs font-bold uppercase text-slate-500">Due Date</th>
                      <th className="text-center px-3 py-2.5 text-xs font-bold uppercase text-slate-500">GST101A</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(gstReport.periods || []).map((p, i) => (
                      <tr key={i} className={`border-t border-[#E2E2DF] hover:bg-slate-50 ${p.invoice_count === 0 && p.expense_count === 0 ? 'opacity-40' : ''}`}>
                        <td className="px-3 py-2.5 font-medium text-slate-800 text-xs">{p.label}</td>
                        <td className="px-3 py-2.5 text-right font-mono-num text-xs">{fmt(p.subtotal)}</td>
                        <td className="px-3 py-2.5 text-right font-mono-num text-xs text-green-700 font-semibold">{fmt(p.paid_subtotal)}</td>
                        <td className="px-3 py-2.5 text-right font-mono-num text-xs text-[#D35400] font-semibold">{fmt(p.paid_gst)}</td>
                        <td className="px-3 py-2.5 text-right font-mono-num text-xs">{fmt(p.expense_excl_gst)}</td>
                        <td className="px-3 py-2.5 text-right font-mono-num text-xs text-purple-600 font-semibold">{fmt(p.expense_gst)}</td>
                        <td className="px-3 py-2.5 text-right font-mono-num text-xs font-bold bg-green-50 text-green-700">{fmt(p.net_gst)}</td>
                        <td className="px-3 py-2.5 text-center text-xs text-slate-500">{p.due_date ? (() => { try { const [y,m,d] = p.due_date.split('-'); return `${d}/${m}/${y}`; } catch { return ''; } })() : ''}</td>
                        <td className="px-3 py-2.5 text-center">
                          <button
                            data-testid={`generate-gst101a-period-${i}`}
                            onClick={() => navigate(`/pdf-viewer/gst-form/${p.start}_${p.end}`)}
                            className="text-[10px] px-2 py-1 rounded bg-[#003366] text-white hover:bg-[#004488] transition-all font-semibold"
                            title={`Generate GST101A for ${p.label}`}
                          >
                            <FileText size={12} className="inline -mt-0.5 mr-0.5" />Form
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {(gstReport.periods || []).length > 0 && (
                    <tfoot className="bg-slate-50 border-t-2 border-[#D35400]">
                      <tr>
                        <td className="px-3 py-2.5 font-bold text-slate-800">TOTAL</td>
                        <td className="px-3 py-2.5 text-right font-bold font-mono-num">{fmt(gstReport.yearly.total_subtotal)}</td>
                        <td className="px-3 py-2.5 text-right font-bold font-mono-num text-green-700">{fmt(gstReport.yearly.total_paid_subtotal)}</td>
                        <td className="px-3 py-2.5 text-right font-bold font-mono-num text-[#D35400]">{fmt(gstReport.yearly.total_gst)}</td>
                        <td className="px-3 py-2.5 text-right font-bold font-mono-num">{fmt(gstReport.yearly.total_expense_excl_gst)}</td>
                        <td className="px-3 py-2.5 text-right font-bold font-mono-num text-purple-600">{fmt(gstReport.yearly.total_expense_gst)}</td>
                        <td className="px-3 py-2.5 text-right font-bold font-mono-num bg-green-50 text-green-700">{fmt(gstReport.yearly.total_net_gst)}</td>
                        <td className="px-3 py-2.5"></td>
                        <td className="px-3 py-2.5"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
